/**
 * ★★ WK — CONTAR comparecimento é POR PESSOA, e a regra mora em UM lugar só.
 *
 * ⚠ NÃO confundir com `leads/comparecimento.js` (WI), que é o vizinho de nome
 * parecido e faz outra coisa: aquele monta os CAMPOS que a baixa GRAVA no lead
 * (`compareceuEm`, `codigoCareflow`, `valorOrcado`, a marca `semOrcamento`).
 * Este aqui só CONTA, e não escreve nada.
 *
 * ★ POR QUE ESTE MÓDULO EXISTE.
 *
 * Até 29/set/2026 a casa tinha TRÊS contagens de comparecimento:
 *   1. Dashboard  (`dashboard/analise.js`)  — por AGENDAMENTO  → 55 em setembro
 *   2. Metas/Placar (`metas/calculo.js`)    — por AGENDAMENTO  → 55
 *   3. Comissão   (`remuneracao/calculo.js`) — por LEAD        → 62
 *
 * Medido: a folha de setembro que o dono fechou gravou **62**
 * (`fechamentos/2026-09.totais.comparecimentos`). Decisão do dono (D1): as telas
 * passam a contar como a COMISSÃO já contava — não é número novo, é o número que
 * já se paga.
 *
 * ★★ E a regra NÃO é copiada para cá: este módulo REUSA `dataComparecimento` de
 * `@/remuneracao/calculo`, que já tem o espelho provado contra o servidor
 * (`remuneracao/espelho.test.js` importa os dois arquivos e exige saída idêntica).
 * Copiar a regra para um quarto lugar seria repetir exatamente o erro que esta
 * peça veio desfazer.
 *
 * ⚠ A regra, dita por extenso, porque a ordem importa: **`compareceuEm` VENCE**;
 * só na ausência dele vale o agendamento `compareceu` MAIS ANTIGO. Não é "o mais
 * antigo dos dois" — se fosse, as telas voltariam a divergir da folha no caso em
 * que o carimbo é posterior ao agendamento.
 */

/** `Timestamp | Date | string` → `Date`, ou null. Cópia mínima: este módulo é
 *  neutro de propósito e não pode importar de `metas/` nem de `remuneracao/`. */
export const paraData = (v) => {
  if (!v) return null;
  if (typeof v.toDate === 'function') return v.toDate();
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};

export const SEM_RESPONSAVEL = { id: '', nome: 'Sem responsável' };

/**
 * `[{ id, nome }]` → `Map(id → nome)`. ★ Mesma razão de `indexarAgendamentosPorLead`:
 * quem credita comparecimento roda dentro de um laço por lead, e reconstruir o
 * conjunto da equipe a cada volta seria O(leads × equipe) de graça.
 *
 * ⚠ Aceita também uma lista de ids crus (strings) — é o que alguns testes têm na mão.
 */
export function indexarEquipe(equipe = []) {
  const mapa = new Map();
  for (const u of equipe ?? []) {
    if (typeof u === 'string') { if (u) mapa.set(u, ''); continue; }
    if (u?.id) mapa.set(u.id, u.nome ?? '');
  }
  return mapa;
}

/**
 * ★★ A REGRA, e ela mora AQUI desde a WK.
 *
 * Veio de `remuneracao/calculo.js` sem uma vírgula de diferença — o espelho do
 * servidor (`remuneracao/espelho.test.js`) continua comparando os dois lados.
 * Mudou de casa porque `metas/calculo.js` precisa dela, e importá-la de
 * `remuneracao/` faria o Placar alcançar o módulo do salário (duas guardas de
 * grafo proíbem).
 *
 * `compareceuEm` é a fonte: a VE o grava nos dois caminhos e não o deixa
 * retroceder, então ele já é o PRIMEIRO comparecimento — que é o que se paga.
 * Reserva para os leads anteriores à VE: o agendamento `compareceu` mais antigo.
 */
export function dataComparecimento(lead, agendamentos = []) {
  const carimbo = paraData(lead?.compareceuEm);
  if (carimbo) return carimbo;

  let maisAntigo = null;
  for (const a of agendamentos ?? []) {
    if (a?.leadId !== lead?.id || a?.status !== 'compareceu') continue;
    const quando = paraData(a.dataHora);
    if (quando && (!maisAntigo || quando < maisAntigo)) maisAntigo = quando;
  }
  return maisAntigo;
}

/**
 * Agrupa agendamentos por lead.
 *
 * ★ Não é enfeite de performance: `dataComparecimento` varre a lista inteira e
 * descarta pelo `leadId`. Chamando-a com a coleção toda para cada lead seriam
 * 7.826 × 2.539 ≈ 20 milhões de iterações **a cada render** do dashboard.
 * Passar só os agendamentos daquele lead é o MESMO resultado (a função filtra
 * por `leadId` de qualquer jeito) em O(n + m).
 */
export function indexarAgendamentosPorLead(agendamentos = []) {
  const mapa = new Map();
  for (const a of agendamentos ?? []) {
    if (!a?.leadId) continue;
    if (!mapa.has(a.leadId)) mapa.set(a.leadId, []);
    mapa.get(a.leadId).push(a);
  }
  return mapa;
}

/**
 * Os comparecimentos de uma janela — **UM POR LEAD**.
 *
 * @param dentro  predicado da janela; recebe a Date do comparecimento.
 * @returns lista de `{ lead, quando }` — quem chama decide se conta, credita ou soma.
 *
 * ⚠⚠ A-10.4 — a saída NÃO carrega os agendamentos do lead, e isso foi medido: ela é
 * serializada dentro de `recortes.compareceram` nos goldens (`vq.antes.json`,
 * `consolidarMes.antes.json`) e dentro de `analisar`. ★ Quem precisa dos agendamentos
 * para creditar chama `indexarAgendamentosPorLead` no próprio laço — é a mesma
 * estrutura, sem engordar a saída de todo mundo.
 */
export function comparecimentosNoPeriodo(leads = [], agendamentos = [], dentro = () => true) {
  const porLead = indexarAgendamentosPorLead(agendamentos);
  const saida = [];
  for (const lead of leads ?? []) {
    const quando = dataComparecimento(lead, porLead.get(lead?.id) ?? []);
    if (!quando || !dentro(quando)) continue;
    saida.push({ lead, quando });
  }
  return saida;
}

/**
 * ★★★ A-10.4 — O CRÉDITO DO COMPARECIMENTO É DE QUEM AGENDOU.
 *
 * ⚠⚠ Isto NÃO é `creditoSdr` (`metas/credito.js`), e não o substitui. `creditoSdr`
 * continua sendo a régua de **leads trabalhados** e de **recebimentos**; só o
 * COMPARECIMENTO troca de dono. Mudar `creditoSdr` por dentro mexeria em tudo de
 * uma vez — decisão do dono, 16/out/2026.
 *
 * ★ A regra, por extenso (decisões finais do dono):
 *
 *  1. o crédito é de quem criou o agendamento em que o paciente COMPARECEU
 *     (`agendadoPorId` do agendamento com `status: 'compareceu'`);
 *  2. faltou e reagendou → vale o agendamento NOVO, o que deu `compareceu`.
 *     Agendamento `faltou`/`cancelado` não credita ninguém (o laço só olha
 *     `compareceu`);
 *  3. dois `compareceu` no mesmo lead → vale o MAIS ANTIGO. ⚠⚠ Não é escolha de
 *     estilo: `dataComparecimento` (acima) já usa "o `compareceu` mais antigo"
 *     como reserva da DATA. Usar o mais recente aqui faria **data e dono virem de
 *     agendamentos diferentes**;
 *  4. agendado pelo GESTOR → crédito do GESTOR: ele é pessoa da equipe;
 *  5. ⚠⚠⚠ `agendadoPorId` que NÃO é pessoa da equipe (`importacao-planilha-…`,
 *     `'sistema'`, vazio, uid que não está em `usuarios` da clínica) → cai no
 *     `lead.sdrId`, o responsável ATUAL. Medido em produção (16/out/2026): dos
 *     1.182 agendamentos `compareceu` da história, **1.021 são da importação** e
 *     **81 estão sem autor** — sem esta reserva, 87 % do passado seria creditado a
 *     um robô.
 *
 * ⚠ A reserva é `sdrId`, **não** `creditoSdr`: o dono pediu o responsável ATUAL.
 *
 * @param equipe  `[{ id, nome }]` dos `usuarios` da clínica, ou o `Map` de
 *                `indexarEquipe` (para quem chama dentro de um laço).
 */
export function creditoComparecimento(lead, agendamentos = [], equipe = []) {
  const daEquipe = equipe instanceof Map ? equipe : indexarEquipe(equipe);

  /**
   * O `compareceu` mais antigo. ★ O desempate por `id` existe porque duplicata
   * do mesmo dia é real (medido: 137 leads com 2+ `compareceu` na história) e a
   * ordem do array não é garantida — sem ele, dois agendamentos com a MESMA
   * `dataHora` e autores diferentes creditariam pessoas diferentes a cada
   * leitura da mesma tela.
   */
  let escolhido = null;
  let maisAntigo = null;
  for (const a of agendamentos ?? []) {
    if (a?.leadId !== lead?.id || a?.status !== 'compareceu') continue;
    const quando = paraData(a.dataHora);
    if (!quando) continue;
    const empate = maisAntigo && quando.getTime() === maisAntigo.getTime();
    if (!maisAntigo || quando < maisAntigo || (empate && String(a.id ?? '') < String(escolhido?.id ?? ''))) {
      maisAntigo = quando;
      escolhido = a;
    }
  }

  const dono = escolhido?.agendadoPorId;
  if (dono && daEquipe.has(dono)) {
    return { id: dono, nome: daEquipe.get(dono) || escolhido.agendadoPorNome || '' };
  }

  if (lead?.sdrId) return { id: lead.sdrId, nome: daEquipe.get(lead.sdrId) || lead.sdrNome || '' };
  return { ...SEM_RESPONSAVEL };
}
